import { motion } from "framer-motion";
import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { sectors, images } from "../../data/content";
import OrbitPattern from "../OrbitPattern";

const ease = [0.22, 1, 0.36, 1];

const Sectors = () => {
  const { isAr, t } = useLang();
  const [hover, setHover] = useState(null);
  return (
    <section
      id="sectors"
      className="relative bg-[#173F35] text-bg-ivory py-[120px] md:py-[180px] overflow-hidden"
      data-testid="sectors-section"
    >
      <div className="absolute -bottom-[260px] -start-[200px]">
        <OrbitPattern size={900} opacity={0.14} />
      </div>

      <div className="relative z-10 max-w-[1760px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex justify-between items-center text-bg-ivory/65 mb-16">
          <span className="page-meta">— 12</span>
          <span className="page-meta">{t("القطاعات التي نخدمها", "Sectors We Serve")}</span>
        </div>

        <div className="grid grid-cols-12 gap-x-10 gap-y-12 items-stretch">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
            className="col-span-12 md:col-span-5"
          >
            <div className="relative overflow-hidden h-[500px] md:h-full min-h-[500px]">
              <img
                src={images.craftsman}
                alt={t("الفحص الدقيق", "Precision Inspection")}
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.9) brightness(0.9)" }}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(23,63,53,0.15), rgba(15,47,41,0.5))" }} />
              <div className="absolute bottom-6 left-6 right-6 flex justify-between text-bg-ivory">
                <span className="label-meta">— QUALITY</span>
                <span className="page-meta">{t("الفحص الدقيق", "Precision Inspection")}</span>
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease }}
              className="mb-12"
            >
              <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-champagne/80 mb-4`}>
                {t("[ القطاعات التي نخدمها ]", "[ SECTORS WE SERVE ]")}
              </div>
              <h3
                className={`${isAr ? "arabic-title" : "serif-title"} text-bg-ivory mb-6`}
                style={{ fontSize: isAr ? "clamp(34px, 4vw, 56px)" : "clamp(40px, 4.6vw, 78px)" }}
              >
                {isAr ? "القطاعات التي نخدمها" : (<>Sectors <em className="font-light">We Serve</em></>)}
              </h3>
              {isAr ? (
                <p className="arabic-title text-bg-ivory/75 max-w-md" dir="rtl" style={{ fontSize: 15, lineHeight: 1.9, fontWeight: 400 }}>
                  تخدم بيج براند مجموعة واسعة من القطاعات ذات المتطلبات العالية من خلال قدرة تشغيلية تضمن تنفيذاً منظماً وتوريداً مرحلياً مستقراً.
                </p>
              ) : (
                <p className="text-bg-ivory/75 max-w-md" style={{ fontSize: 15, lineHeight: 1.8 }}>
                  We serve a wide range of high-demand sectors through operational capability that ensures organized execution and reliable phased supply.
                </p>
              )}
            </motion.div>

            <div className="editorial-rule-dark mb-10" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {sectors.map((s, i) => (
                <motion.div
                  key={s.code}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease, delay: i * 0.1 }}
                  onMouseEnter={() => setHover(s.code)}
                  onMouseLeave={() => setHover(null)}
                  className={`relative p-6 cursor-hover transition-all duration-700 ${i !== 2 ? "md:border-e" : ""}`}
                  style={{ borderColor: "rgba(232,211,195,0.22)" }}
                  data-testid={`sector-${s.code}`}
                >
                  <div
                    className="w-14 h-14 rounded-full border flex items-center justify-center mb-8 transition-colors duration-700"
                    style={{
                      borderColor: hover === s.code ? "#E8D3C3" : "rgba(232,211,195,0.4)",
                      background: hover === s.code ? "rgba(232,211,195,0.1)" : "transparent",
                    }}
                  >
                    <span className="page-meta text-champagne">{s.code}</span>
                  </div>
                  <div
                    className={`${isAr ? "arabic-title" : "serif-title italic font-light"} text-bg-ivory mb-6`}
                    style={{ fontSize: isAr ? 20 : 22 }}
                  >
                    {isAr ? s.ar_title : s.en_title}
                  </div>
                  <div className="editorial-rule-dark mb-4" />
                  <ul className="space-y-2.5">
                    {s.items.map((it, j) => (
                      <li
                        key={j}
                        className={`${isAr ? "arabic-title" : ""} text-bg-ivory/80`}
                        dir={isAr ? "rtl" : "ltr"}
                        style={{ fontSize: 13, lineHeight: 1.7, fontWeight: 400 }}
                      >
                        {isAr ? `• ${it.ar}` : `• ${it.en}`}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Sectors;
